import React, { RefObject, useState, useRef, useEffect } from 'react';
import {
  ButtonBubble,
  ButtonBubbleTextIcon
} from '../../../../../patterns/00-atoms/button/button.tsx';
import { RestaurantDetails_getRestaurantDetails } from '../../../../../types/graphql-types-generated.ts';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import { restaurantImageOrDefault } from '../../../../../utils/restaurant-image-or-default.ts';
import { ModalRef } from '../../../../../patterns/01-molecules/modal/modal.tsx';
import { likeRestaurant } from './like-restaurant.graphql.ts';
import { useAppContext } from '../../../../../components/app-context/app-context.tsx';
import { captionText } from '../../../../../utils/lightbox/caption.ts';
import { RestaurantInfoModalView } from '../../02-organisms/restaurant-info-modal/restaurant-info-modal.tsx';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { hasReservation } from '../../../../../utils/reservation.ts';
import { useIsSsr } from '../../../../../utils/hooks/use-is-ssr.tsx';

export type RestaurantCoverProps = {
  restaurantDetails: RestaurantDetails_getRestaurantDetails;
  modalRef: RefObject<ModalRef | null>;
  setInfoModalDefaultView?: (view: RestaurantInfoModalView) => void;
  setShowStickyAd?: (show: boolean) => void;
};

export const RestaurantCover: React.FunctionComponent<RestaurantCoverProps> = props => {
  const likeRestaurantButtonRef = useRef<HTMLButtonElement>(null);
  const reservationButtonRef = useRef<HTMLButtonElement>(null);
  const openLightboxButtonRef = useRef<HTMLButtonElement>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const { appContext } = useAppContext();
  const { isSsr } = useIsSsr();
  const didRun = useRef(false);

  const handleLike = () => {
    likeRestaurant({ restaurantId: props.restaurantDetails.id }, appContext)
      .then(() => {
        UBTracking.trackEvent.click('restaurant_header', 'restaurant_like_button');
        window.location.href = 'https://www.speisekarte.de/anmelden';
      })
      .catch(() => {
        // ... 🤷🏻‍♂️
      });
  };

  const handleOpenLightbox = () => {
    setGalleryOpen(true);
    UBTracking.trackEvent.click('restaurant_header', 'lightbox', 'open');
  };

  const handleCloseLightbox = () => {
    setGalleryOpen(false);
    UBTracking.trackEvent.click('restaurant_header', 'lightbox', 'close');
  };

  useEffect(() => {
    if (isSsr || didRun.current) {
      return;
    }

    if (likeRestaurantButtonRef.current) {
      UBTracking.onTrackTargetView(
        likeRestaurantButtonRef.current,
        'restaurant_header',
        'restaurant_like_button'
      );
    }

    if (reservationButtonRef.current) {
      UBTracking.onTrackTargetView(
        reservationButtonRef.current,
        'restaurant_header',
        'reservation',
        'open'
      );
    }

    if (openLightboxButtonRef.current) {
      UBTracking.onTrackTargetView(
        openLightboxButtonRef.current,
        'restaurant_header',
        'lightbox',
        'open'
      );
    }

    didRun.current = true;
  });

  return (
    <>
      <div className="bg-sk-red-500 relative flex min-h-24 justify-center overflow-hidden p-2 md:min-h-48 md:rounded-t-3xl md:p-4">
        <picture>
          <source
            srcSet={restaurantImageOrDefault(props.restaurantDetails.profileImageUrl, 'webp')}
            type="image/webp"
          />
          <source
            srcSet={restaurantImageOrDefault(props.restaurantDetails.profileImageUrl, 'avif')}
            type="image/avif"
          />
          <img
            className="absolute inset-0 z-0 h-full w-full object-cover"
            src={restaurantImageOrDefault(props.restaurantDetails.profileImageUrl)}
            alt={`Profilbild von ${props.restaurantDetails.name}`}
          />
        </picture>
        <div className="relative z-10 flex grow justify-between">
          <div>
            {props.restaurantDetails.gallery && props.restaurantDetails.gallery.length > 0 && (
              <ButtonBubbleTextIcon
                ref={openLightboxButtonRef}
                onClick={handleOpenLightbox}
                iconName="image"
              >
                <span>{props.restaurantDetails.gallery.length}</span>
              </ButtonBubbleTextIcon>
            )}
          </div>
          <div className="flex flex-row items-start gap-2">
            {hasReservation(props.restaurantDetails) && (
              <ButtonBubble
                ref={reservationButtonRef}
                onClick={() => {
                  UBTracking.trackEvent.click('restaurant_header', 'reservation', 'open');
                  props.setInfoModalDefaultView &&
                    props.setInfoModalDefaultView({ type: 'reserve' });
                  props.setShowStickyAd && props.setShowStickyAd(true);
                  props.modalRef.current?.openModal();
                }}
              >
                Reservieren
              </ButtonBubble>
            )}
            <ButtonBubbleTextIcon
              ref={likeRestaurantButtonRef}
              onClick={handleLike}
              iconName="like"
            >
              <span>{props.restaurantDetails.likesCount}</span>
            </ButtonBubbleTextIcon>
          </div>
        </div>
      </div>
      {props.restaurantDetails.gallery && props.restaurantDetails.gallery.length > 0 && (
        <Lightbox
          open={galleryOpen}
          close={handleCloseLightbox}
          slides={props.restaurantDetails.gallery.map(image => ({
            src: image.url,
            description: captionText(image, props.restaurantDetails)
          }))}
          captions={{ descriptionTextAlign: 'center' }}
          plugins={[Captions]}
        />
      )}
    </>
  );
};
